/**
 * FixedRateAssetCard — Oct 2026.
 *
 * The "Asset Class" educational card for fixed-rate securities (baby
 * bonds, preferred shares / depositary shares, treasury-vehicle
 * preferreds). Shared by the Stock Modal Insights tab and the SEO
 * pages (/stock/:t, /is/:t/a-dividend-stock, /ticker/:t/ex-dividend-date)
 * so every surface shows the same par value, coupon, cumulative status
 * and admin-confirmed call terms.
 */
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

const FIXED_RATE_CLASSES = new Set(['baby_bond', 'preferred_share', 'preferred_unit']);

export function isFixedRateAsset(stock) {
  if (!stock) return false;
  return FIXED_RATE_CLASSES.has(stock.asset_class) || stock.is_preferred === true;
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function fmtPct(rate) {
  const pct = rate * 100;
  return `${pct.toFixed(pct % 1 ? 3 : 2).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

export function fixedRateTerms(stock) {
  const isBond = stock.asset_class === 'baby_bond';
  const parKnown = Number(stock.par_value) > 0;
  const par = parKnown ? Number(stock.par_value) : (stock.is_treasury_vehicle ? 100 : 25);
  const px = Number(stock.price ?? stock.current_price) || 0;
  const divRate = Number(stock.dividend_rate) || 0;
  const coupon = Number(stock.coupon_rate) > 0
    ? Number(stock.coupon_rate)
    : (parKnown && divRate > 0 ? divRate / par : null);
  const variable = stock.variable_rate === true || stock.not_fixed_rate === true;
  const floating = /floating/i.test(stock.long_name || stock.name || '');
  const floatBenchmark = stock.float_benchmark || null;
  const floatSpread = Number(stock.float_spread_pct) > 0 ? Number(stock.float_spread_pct) : null;
  const statedCoupon = Number(stock.coupon_rate) > 0 ? Number(stock.coupon_rate) : null;
  const rawCall = stock.preferred_call_date || null;
  const issuerChoice = rawCall === 'issuer-choice';
  const callIso = issuerChoice ? null : rawCall;
  const todayIso = new Date().toISOString().slice(0, 10);
  const callPast = !!callIso && callIso <= todayIso;
  const abovePar = px > 0 && px > par * 1.002;
  const termYear = stock.preferred_term_year || stock.bond_maturity_year || null;
  return { isBond, parKnown, par, px, coupon, variable, floating, floatBenchmark, floatSpread, statedCoupon, issuerChoice, callIso, callPast, abovePar, termYear };
}

// Yield-to-call (approximate, annual): (C + (F − P)/n) / ((F + P)/2).
// Returns {mode:'ytc', pct, years} | {mode:'now', pct} (immediate return
// if redeemed today — callable now / issuer's choice) | null (no call date).
export function yieldToCall(stock, t = fixedRateTerms(stock)) {
  if (!t.parKnown || !(t.px > 0)) return null;
  if (!t.callIso && !t.issuerChoice) return null;
  const F = t.par;
  const P = t.px;
  const C = (Number(stock.dividend_rate) > 0 ? Number(stock.dividend_rate) : (t.coupon ? t.coupon * F : 0));
  const years = t.callIso ? (new Date(`${t.callIso}T00:00:00Z`).getTime() - Date.now()) / (365.25 * 86400000) : 0;
  if (t.issuerChoice || t.callPast || years < 0.1) {
    return { mode: 'now', pct: ((F - P) / P) * 100 };
  }
  return { mode: 'ytc', pct: ((C + (F - P) / years) / ((F + P) / 2)) * 100, years };
}

export default function FixedRateAssetCard({ stock, className = '', onNavigateWithOrigin = null }) {
  if (!isFixedRateAsset(stock)) return null;
  const t = fixedRateTerms(stock);
  const parStr = `$${t.par.toFixed(2)}`;
  const diff = t.px > 0 ? t.px - t.par : null;
  const cumulative = stock.preferred_cumulative;
  const callDateHuman = fmtDate(t.callIso);
  const divRate = Number(stock.dividend_rate) || 0;
  const liveCoupon = t.parKnown && divRate > 0 ? divRate / t.par : null;
  const ytc = yieldToCall(stock, t);
  const curYield = t.px > 0 && divRate > 0 ? (divRate / t.px) * 100 : null;

  let callTone = 'neutral';
  let callHeadline;
  let callBody;
  if (t.callPast && t.abovePar) {
    callTone = 'danger';
    callHeadline = `Callable NOW at ${parStr} — trading above par`;
    callBody = `Call protection ended ${callDateHuman}. The issuer can redeem at ${parStr} at any time; at $${t.px.toFixed(2)} that is an instant loss of $${diff.toFixed(2)} per share.`;
  } else if (t.callPast) {
    callTone = 'warn';
    callHeadline = `Callable now at ${parStr}`;
    callBody = `Call protection ended ${callDateHuman}. The issuer may redeem at par at any time${diff != null && diff < 0 ? ` — below par today, so a call would pay you the $${Math.abs(diff).toFixed(2)} difference` : ''}.`;
  } else if (t.issuerChoice) {
    callTone = t.abovePar ? 'warn' : 'neutral';
    callHeadline = `Callable at the issuer's choice`;
    callBody = `No fixed call date — the prospectus allows redemption at ${parStr} at any time on 15–60 business-days notice${t.abovePar ? `. Trading $${diff.toFixed(2)} above par, a call would hand you that loss` : ''}.`;
  } else if (t.callIso) {
    callTone = t.abovePar ? 'warn' : 'neutral';
    callHeadline = `Call protection until ${callDateHuman}`;
    callBody = `From ${callDateHuman} the issuer can redeem at ${parStr}/share${t.abovePar ? ` — you are paying $${diff.toFixed(2)} above that today` : ''}.`;
  } else {
    callHeadline = 'Call date not yet confirmed';
    callBody = `Issuer can buy these back at ${parStr}/share early if interest rates drop. Check the prospectus for the first call date.`;
  }

  const toneCls = callTone === 'danger'
    ? 'border-red-400/70 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-200'
    : callTone === 'warn'
      ? 'border-amber-400/70 bg-amber-100/70 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200'
      : 'border-amber-200/60 dark:border-amber-800/40 bg-white/60 dark:bg-black/20 text-amber-800/80 dark:text-amber-300/70';

  const title = t.isBond
    ? 'Asset Class: Exchange-Traded Note (Baby Bond)'
    : stock.is_treasury_vehicle
      ? `Asset Class: Treasury-Vehicle Preferred${stock.preferred_series ? ` — Series ${stock.preferred_series}` : ''}`
      : `Asset Class: Preferred Stock${stock.preferred_series ? ` — Series ${stock.preferred_series}` : ''}`;

  const edgarSearchUrl = `https://www.sec.gov/edgar/search/#/q=%22${encodeURIComponent(stock.ticker || '')}%22&forms=424B2%2C424B5%2C424B4%2C424B3%2C424B7%2CS-3ASR%2CFWP`;
  const edgarCompanyUrl = `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${encodeURIComponent(stock.preferred_parent_ticker || stock.ticker || '')}&type=424B&dateb=&owner=include&count=40`;
  const edgarLinks = (
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
      <a
        href={edgarSearchUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid"
        title="SEC EDGAR full-text search for this ticker's prospectus supplements (424B) — the call date, par value and any floating-rate spread are in the 'Description of the Series' section."
        data-testid="fixed-rate-edgar-search-link"
      >
        Look up the prospectus on EDGAR ↗
      </a>
      <a
        href={edgarCompanyUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="underline decoration-dotted underline-offset-2 hover:decoration-solid opacity-80"
        title={`Browse ${stock.preferred_parent_ticker || stock.ticker}'s 424B prospectus filings on EDGAR.`}
        data-testid="fixed-rate-edgar-company-link"
      >
        Issuer filings ↗
      </a>
    </div>
  );

  return (
    <div
      className={`p-4 rounded-lg border border-amber-300/60 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 ${className}`}
      data-testid={t.isBond ? 'baby-bond-notice' : 'preferred-share-notice'}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">💡</span>
        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">{title}</h4>
      </div>
      <p className="text-xs text-amber-800 dark:text-amber-300/90 leading-relaxed">
        {t.isBond ? (
          <>
            This ticker is a corporate <strong>debt</strong> security — not equity. You are lending money to the
            issuer, who pays a fixed{t.coupon ? <> <strong>{fmtPct(t.coupon)} annual coupon</strong></> : ' coupon'}
            {' '}on its <strong>{parStr} par value</strong>
            {t.termYear ? <> until it matures on <strong>Dec 31, {t.termYear}</strong>, when the full principal is returned.</> : '.'}
          </>
        ) : (
          <>
            This ticker is a <strong>preferred share</strong>
            {stock.is_treasury_vehicle ? ' issued by a treasury-strategy company' : ' (often a depositary share representing 1/1,000th of a high-priced preferred)'}.
            It&apos;s equity, but it pays a{' '}
            {t.variable
              ? <><strong>variable-rate dividend</strong> re-set by the board</>
              : t.floating
                ? <>
                  <strong>fixed-to-floating dividend</strong>
                  {t.statedCoupon ? <> — <strong>{fmtPct(t.statedCoupon)} fixed</strong>{callDateHuman ? <> {t.callPast ? 'until' : 'through'} {callDateHuman}</> : ' for the initial period'}</> : null}
                  {t.floatBenchmark || t.floatSpread
                    ? <>, {t.callPast || !callDateHuman ? 'now resetting at' : 'then resets to'} <strong>{t.floatBenchmark || 'benchmark'}{t.floatSpread ? ` + ${t.floatSpread.toFixed(3)}%` : ''}</strong></>
                    : <>, then floats with short-term rates</>}
                  {liveCoupon && (t.callPast || !callDateHuman) ? <> (≈ <strong>{fmtPct(liveCoupon)} on par</strong> at the current payout)</> : null}
                </>
                : <>fixed{t.coupon ? <> <strong>{fmtPct(t.coupon)} annual dividend</strong></> : ' dividend'}</>}
            {' '}on its <strong>{parStr} par value</strong>
            {' — '}
            <strong>{cumulative === false ? 'non-cumulative' : cumulative === true ? 'cumulative' : t.variable ? 'board-set' : 'fixed-rate'}</strong>.
            {cumulative === false ? ' Skipped quarters are gone for good — the issuer is not required to make them up.' : ''}
            {cumulative === true ? ' Missed dividends accrue and must be paid before any common dividend.' : ''}
          </>
        )}
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]">
        <div className="p-2 rounded bg-white/60 dark:bg-black/20 border border-amber-200/60 dark:border-amber-800/40" data-testid="fixed-rate-tile-income">
          <div className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">{t.variable ? 'Variable Rate' : t.floating ? 'Fixed-to-Floating' : 'Fixed Income'}</div>
          <div className="text-amber-800/80 dark:text-amber-300/70">
            {t.isBond
              ? 'Payout is a legally obligated coupon, not a fluctuating company dividend.'
              : t.variable
                ? 'The board re-sets the rate (often monthly); payout is senior to common but not guaranteed.'
                : t.floating
                  ? (t.floatBenchmark || t.floatSpread
                    ? `Resets to ${t.floatBenchmark || 'a short-term benchmark'}${t.floatSpread ? ` + ${t.floatSpread.toFixed(3)}%` : ''} ${t.callPast || !callDateHuman ? 'each period' : `once the fixed period ends on ${callDateHuman}`} — payout rises and falls with the Fed, not with earnings.`
                    : 'Paid a fixed coupon through the first call date, then resets to a benchmark (SOFR/LIBOR) plus a spread — so the payout now moves with short-term rates.')
                  : 'Payout is a contractual dividend with priority over common stock — but not legally guaranteed.'}
          </div>
        </div>
        <div className="p-2 rounded bg-white/60 dark:bg-black/20 border border-amber-200/60 dark:border-amber-800/40" data-testid="fixed-rate-tile-term">
          <div className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
            {t.isBond ? 'Maturity Return' : t.termYear ? 'Term Redemption' : 'Perpetual'}
          </div>
          <div className="text-amber-800/80 dark:text-amber-300/70">
            {t.isBond
              ? `Issuer must repay the full ${parStr} face value per share on the maturity date.`
              : t.termYear
                ? `Term preferred — the issuer must redeem at ${parStr}/share by ${t.termYear}, so par is returned on a known horizon.`
                : 'No maturity date — these can pay forever, but they sit BELOW debt in the capital stack.'}
          </div>
        </div>
        <div className={`p-2 rounded border ${toneCls}`} data-testid="fixed-rate-tile-call">
          <div className="font-semibold mb-0.5">{callTone === 'danger' ? '⚠ ' : ''}Call Risk</div>
          <div className="font-semibold" data-testid="fixed-rate-call-headline">{callHeadline}</div>
          <div className="opacity-90" data-testid="fixed-rate-call-body">{callBody}</div>
          {!t.callIso && !t.issuerChoice && edgarLinks}
        </div>
        <div className="p-2 rounded bg-white/60 dark:bg-black/20 border border-amber-200/60 dark:border-amber-800/40" data-testid="fixed-rate-tile-par">
          <div className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">Price vs Par</div>
          <div className="text-amber-800/80 dark:text-amber-300/70">
            {t.px > 0 ? (
              <>
                <strong className="tabular-nums">${t.px.toFixed(2)}</strong> vs {parStr} par
                {diff != null && Math.abs(diff) >= 0.005 ? (
                  <> — <strong className={diff > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}>
                    ${Math.abs(diff).toFixed(2)} {diff > 0 ? 'above' : 'below'} par
                  </strong> ({((diff / t.par) * 100).toFixed(1)}%)</>
                ) : ' — at par'}
                {!t.parKnown ? ' · par assumed' : ''}
                {ytc && (
                  <div className="mt-1" data-testid="fixed-rate-ytc">
                    {curYield != null ? <>Current yield <strong className="tabular-nums">{curYield.toFixed(2)}%</strong> · </> : null}
                    {ytc.mode === 'ytc' ? (
                      <>Yield to call <strong className={`tabular-nums ${ytc.pct < (curYield ?? 0) ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{ytc.pct.toFixed(2)}%</strong> over {ytc.years.toFixed(1)} yrs{ytc.pct < (curYield ?? 0) ? ' — the above-par premium erodes your return if called' : ''}</>
                    ) : (
                      <>If called today <strong className={`tabular-nums ${ytc.pct < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{ytc.pct >= 0 ? '+' : ''}{ytc.pct.toFixed(2)}%</strong> on your price{ytc.pct < 0 ? ' — instant loss' : ' — par would pay you the gap'}</>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>Par {parStr}{!t.parKnown ? ' (assumed)' : ''} — live price unavailable.</>
            )}
          </div>
        </div>
      </div>
      {stock.prospectus_quote && (stock.prospectus_quote.call || stock.prospectus_quote.par || stock.prospectus_quote.float) && (
        <div className="mt-3 space-y-1" data-testid="fixed-rate-prospectus-quote">
          {[['Optional redemption', stock.prospectus_quote.call], ['Par value', stock.prospectus_quote.par], ['Floating-rate reset', stock.prospectus_quote.float]]
            .filter(([, q]) => !!q)
            .map(([label, q]) => (
              <blockquote key={label} className="text-[10px] leading-snug border-l-2 border-amber-400/70 pl-2 text-amber-900/85 dark:text-amber-200/80 italic">
                <span className="not-italic font-semibold mr-1">{label} — from the prospectus:</span>
                &ldquo;{q}&rdquo;
              </blockquote>
            ))}
          {stock.prospectus_quote.doc_url && (
            <a
              href={stock.prospectus_quote.doc_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-block text-[10px] font-semibold text-amber-800 dark:text-amber-300 underline decoration-dotted underline-offset-2 hover:decoration-solid"
              data-testid="fixed-rate-prospectus-source-link"
            >
              Source: SEC filing{stock.prospectus_quote.source ? ` ${stock.prospectus_quote.source}` : ''} ↗
            </a>
          )}
        </div>
      )}
      <p className="mt-3 text-[10px] text-amber-700/80 dark:text-amber-400/60 italic">
        {t.variable
          ? <>Dividends are paid on the {parStr} par value at a board-set rate, so the yield moves with each re-set. Payout Ratio is N/A — not funded by operating earnings.</>
          : t.floating
            ? <>Yield is computed against the {parStr} par value; the rate now resets with short-term benchmarks, so it moves with the Fed rather than with earnings. Payout Ratio is N/A — fixed-income equity.</>
            : <>Yield is computed against the {parStr} par value, not the live market price, so it stays fixed for the life of the {t.isBond ? 'bond' : 'preferred'}. Payout Ratio is N/A — fixed-rate {t.isBond ? 'debt instruments' : 'equity'}.</>}
      </p>
      {!t.isBond && (
        <div className="mt-3 flex justify-center">
          {typeof onNavigateWithOrigin === 'function' ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNavigateWithOrigin('/learn/preferred-stock-tickers');
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200 hover:underline decoration-dotted underline-offset-2 cursor-pointer"
              data-testid="preferred-learn-more-link"
              title="Open the Learn guide: Decoding Preferred Tickers. Back button returns here with this stock modal reopened."
            >
              <BookOpen className="w-3.5 h-3.5" />
              Read more about Preferred Stocks
            </button>
          ) : (
            <Link
              to="/learn/preferred-stock-tickers"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200 hover:underline decoration-dotted underline-offset-2"
              data-testid="preferred-learn-more-link"
              title="Open the Learn guide: Decoding Preferred Tickers."
            >
              <BookOpen className="w-3.5 h-3.5" />
              Read more about Preferred Stocks
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
